import { useState, useMemo } from 'react'
import type { QuestionBreakdown, ActiveFilter } from '../types/admin'

const PROGRESS_COLORS = ['#a855f7', '#6366f1', '#3b82f6', '#ec4899', '#8b5cf6', '#f472b6', '#818cf8', '#c084fc']

interface QuestionChartProps {
  breakdown: QuestionBreakdown
  questionLabel: string
  textResponses?: Array<{ respondentName: string; value: string }>
  onFilterSelect: (filter: ActiveFilter) => void
  activeFilters: ActiveFilter[]
}

export function QuestionChart({ breakdown, questionLabel, textResponses, onFilterSelect, activeFilters }: QuestionChartProps) {
  const [isOpen, setIsOpen] = useState(false)

  const isFiltered = (value: string) =>
    activeFilters.some((f) => f.questionId === breakdown.questionId && f.value === value)

  const sortedDistribution = useMemo(() => {
    return Object.entries(breakdown.distribution)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [breakdown.distribution])

  const maxCount = useMemo(() => Math.max(...sortedDistribution.map((d) => d.count), 1), [sortedDistribution])

  if (breakdown.type === 'text') {
    return (
      <div className="question-chart-card">
        <button className="question-chart-header" onClick={() => setIsOpen(!isOpen)}>
          <span className="question-chart-title">{questionLabel}</span>
          <div className="question-chart-header-right">
            <span className="question-type-badge">Text</span>
            <span className="question-chart-count">{breakdown.totalAnswers}</span>
            <span className="expand-icon">{isOpen ? '\u25B2' : '\u25BC'}</span>
          </div>
        </button>
        <div className={`question-chart-panel ${isOpen ? 'open' : ''}`}>
          <div className="question-chart-panel-inner">
            <ul className="text-responses-list">
              {(textResponses || []).slice(0, 20).map((r, i) => (
                <li key={i} className="text-response-item">
                  <span className="text-response-author">{r.respondentName || 'Anonymous'}</span>
                  <span className="text-response-value">{r.value}</span>
                </li>
              ))}
              {(textResponses || []).length === 0 && (
                <li className="text-response-item text-response-empty">No text responses</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="question-chart-card">
      <button className="question-chart-header" onClick={() => setIsOpen(!isOpen)}>
        <span className="question-chart-title">{questionLabel}</span>
        <div className="question-chart-header-right">
          <span className="question-type-badge">{breakdown.type === 'rating' ? 'Rating' : 'Choice'}</span>
          <span className="question-chart-count">{breakdown.totalAnswers}</span>
          <span className="expand-icon">{isOpen ? '\u25B2' : '\u25BC'}</span>
        </div>
      </button>
      <div className={`question-chart-panel ${isOpen ? 'open' : ''}`}>
        <div className="question-chart-panel-inner">
          {breakdown.type === 'rating' && breakdown.rating.average !== null && (
            <div className="rating-summary">
              <span>Avg: <strong>{breakdown.rating.average}</strong></span>
              <span>Min: {breakdown.rating.min}</span>
              <span>Max: {breakdown.rating.max}</span>
            </div>
          )}
          <div className="progress-bars">
            {sortedDistribution.map((d, i) => {
              const pct = Math.round((d.count / breakdown.totalAnswers) * 100)
              const filtered = isFiltered(d.name)
              return (
                <button
                  key={d.name}
                  className={`progress-row ${filtered ? 'filtered' : ''}`}
                  onClick={() => onFilterSelect({ questionId: breakdown.questionId, questionLabel, value: d.name })}
                >
                  <span className="progress-label">{d.name}</span>
                  <div className="progress-track">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${(d.count / maxCount) * 100}%`,
                        background: filtered ? '#22c55e' : PROGRESS_COLORS[i % PROGRESS_COLORS.length],
                      }}
                    />
                  </div>
                  <span className="progress-stat">{d.count} <span className="progress-pct">({pct}%)</span></span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
